import { bytesToBase64, base64ToBytes } from '../crypto/base64.ts'
import { generateKeyPair, importPrivateKey, importPublicKey } from '../crypto/ZionCipher.ts'

export interface EphemeralKeyPair {
  publicKeyBase64: string
  privateKey: CryptoKey
}

/** Copia su ArrayBuffer “puro” — WebKit rifiuta view condivise/offset. */
function cryptoBytes(src: Uint8Array): Uint8Array<ArrayBuffer> {
  return new Uint8Array(src)
}

export async function generateEphemeralKeyPair(): Promise<EphemeralKeyPair> {
  try {
    const pair = await generateKeyPair()
    return { publicKeyBase64: pair.publicKeyBase64, privateKey: pair.privateKey }
  } catch (err) {
    console.error('[ZION] generateEphemeralKeyPair failed', err)
    throw new Error('ephemeral_keys_failed')
  }
}

export async function importEphemeralPrivateKey(base64Pkcs8: string): Promise<CryptoKey> {
  return importPrivateKey(base64Pkcs8)
}

async function deriveAesFromEcdh(myPrivate: CryptoKey, theirPublicB64: string): Promise<CryptoKey> {
  const theirPublic = await importPublicKey(theirPublicB64)
  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: theirPublic },
    myPrivate,
    256,
  )
  const raw = cryptoBytes(new Uint8Array(sharedBits))
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt',
  ])
}

export async function generateDeviceLinkKey(): Promise<{ key: CryptoKey; base64: string }> {
  const raw = cryptoBytes(crypto.getRandomValues(new Uint8Array(32)))
  const key = await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt',
  ])
  return { key, base64: bytesToBase64(raw) }
}

export async function importDeviceLinkKey(base64: string): Promise<CryptoKey> {
  const raw = cryptoBytes(base64ToBytes(base64))
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt',
  ])
}

export async function encryptWithAesGcm(key: CryptoKey, plaintext: Uint8Array): Promise<string> {
  const iv = cryptoBytes(crypto.getRandomValues(new Uint8Array(12)))
  const plain = cryptoBytes(plaintext)
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain)
  const out = new Uint8Array(iv.length + cipher.byteLength)
  out.set(iv, 0)
  out.set(new Uint8Array(cipher), iv.length)
  return bytesToBase64(out)
}

export async function decryptWithAesGcm(key: CryptoKey, payloadB64: string): Promise<Uint8Array> {
  const packed = cryptoBytes(base64ToBytes(payloadB64))
  if (packed.length < 13) throw new Error('payload_too_short')
  const iv = cryptoBytes(packed.subarray(0, 12))
  const cipher = cryptoBytes(packed.subarray(12))
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher)
  return new Uint8Array(plain)
}

/** Cifra vault con segreto ECDH (pairing iniziale). */
export async function encryptVaultForPairing(
  ephemeralPrivate: CryptoKey,
  receiverEphemeralPubB64: string,
  plaintext: Uint8Array,
): Promise<string> {
  const aes = await deriveAesFromEcdh(ephemeralPrivate, receiverEphemeralPubB64)
  return encryptWithAesGcm(aes, plaintext)
}

/** Decifra vault ricevuto sul nuovo dispositivo. */
export async function decryptVaultFromPairing(
  ephemeralPrivate: CryptoKey,
  senderEphemeralPubB64: string,
  payloadB64: string,
): Promise<Uint8Array> {
  const aes = await deriveAesFromEcdh(ephemeralPrivate, senderEphemeralPubB64)
  return decryptWithAesGcm(aes, payloadB64)
}

/** Cifra patch cronologia con Device-Link Key stabile. */
export async function encryptHistoryPatch(deviceLinkKey: CryptoKey, plaintext: Uint8Array): Promise<string> {
  return encryptWithAesGcm(deviceLinkKey, plaintext)
}

export async function decryptHistoryPatch(deviceLinkKey: CryptoKey, payloadB64: string): Promise<Uint8Array> {
  return decryptWithAesGcm(deviceLinkKey, payloadB64)
}
